import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { apiErrorMessage } from '../../../utils/apiError';
import { buildSampleValues } from '../../../utils/sampleValues';

// Testing aid, hidden in production builds.
const SHOW_SAMPLE = process.env.NODE_ENV !== 'production';

/**
 * Schema-driven create/edit dialog.
 *
 * fields: [{ name, label, type, required, options }]
 *   type: 'text' | 'number' | 'date' | 'checkbox' | 'select'
 *
 * `onSubmit(values)` should throw to keep the dialog open on failure; the
 * thrown error's server message is shown inline.
 */
const RecordFormModal = ({ show, title, fields, initial, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);

  // Reset whenever the dialog is opened for a different record.
  useEffect(() => {
    if (!show) return;
    const seed = {};
    fields.forEach((f) => {
      const raw = initial?.[f.name];
      if (f.type === 'checkbox') seed[f.name] = Boolean(raw);
      else if (f.type === 'date') seed[f.name] = raw ? String(raw).slice(0, 10) : '';
      // A file input cannot be pre-filled; start empty and treat "no pick" as
      // "keep the existing file".
      else if (f.type === 'file') seed[f.name] = null;
      else if (f.type === 'permissions') {
        // Start from the record's matrix, filling in any module it lacks.
        const current = Array.isArray(raw) ? raw : [];
        seed[f.name] = (f.modules || []).map((module) => {
          const row = current.find((p) => p.module === module);
          return {
            module,
            canView: Boolean(row?.canView),
            canCreate: Boolean(row?.canCreate),
            canEdit: Boolean(row?.canEdit),
            canDelete: Boolean(row?.canDelete),
          };
        });
      } else seed[f.name] = raw ?? '';
    });
    setValues(seed);
    setErrors({});
    setFailure(null);
  }, [show, initial, fields]);

  const change = (e) => {
    const { name, value, type, checked, files } = e.target;
    let next = value;
    if (type === 'checkbox') next = checked;
    else if (type === 'file') next = files?.[0] || null;

    setValues((prev) => ({ ...prev, [name]: next }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const fillSample = () => {
    setValues((prev) => buildSampleValues(fields, prev));
    setErrors({});
    setFailure(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    const found = {};
    fields.forEach((f) => {
      if (!f.required) return;
      const value = values[f.name];
      const empty = f.type === 'file' ? !value : String(value ?? '').trim() === '';
      if (empty) found[f.name] = t('tree.required');
    });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    setFailure(null);
    try {
      await onSubmit(values);
    } catch (err) {
      console.error('Save failed:', err);
      setFailure(apiErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      title={title}
      onClose={onClose}
      footer={
        <>
          {SHOW_SAMPLE && (
            <button
              type="button"
              className="ad-btn ad-btn-ghost"
              onClick={fillSample}
              disabled={saving}
            >
              <i className="fas fa-wand-magic-sparkles"></i>
              {t('property.intake.fillSample')}
            </button>
          )}
          <button type="button" className="ad-btn" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="ad-record-form"
            className="ad-btn ad-btn-primary"
            disabled={saving}
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </>
      }
    >
      <form id="ad-record-form" onSubmit={submit}>
        {failure && <p className="ad-error-text">{failure}</p>}

        {/* Two fields per row; checkboxes span the full width. */}
        <div className="ad-modal-grid">
          {fields.map((f) =>
            f.type === 'permissions' ? (
              <PermissionMatrix
                key={f.name}
                label={f.label}
                modules={f.modules || []}
                moduleLabel={f.moduleLabel}
                value={values[f.name] || []}
                onChange={(rows) => setValues((prev) => ({ ...prev, [f.name]: rows }))}
              />
            ) : f.type === 'checkbox' ? (
              <label key={f.name} className="ad-modal-check">
                <input
                  type="checkbox"
                  name={f.name}
                  className="ad-checkbox"
                  checked={Boolean(values[f.name])}
                  onChange={change}
                />
                {f.icon && <i className={`fas ${f.icon}`}></i>}
                {f.label}
              </label>
            ) : (
              <div
                key={f.name}
                className={`ad-modal-field ${
                  f.type === 'file' || f.full ? 'ad-modal-field-full' : ''
                }`}
              >
                <label
                  className={`ad-modal-label ${f.required ? 'ad-label-required' : ''}`}
                  htmlFor={`f-${f.name}`}
                >
                  {f.label}
                </label>
                <span className="ad-input-wrap">
                  {f.icon && <i className={`fas ${f.icon} ad-input-icon`}></i>}
                  {f.type === 'select' ? (
                    <select
                      id={`f-${f.name}`}
                      name={f.name}
                      className={`ad-input-sm ${f.icon ? 'ad-input-has-icon' : ''} ${
                        errors[f.name] ? 'ad-input-error' : ''
                      }`}
                      value={values[f.name] ?? ''}
                      onChange={change}
                    >
                      <option value="">{f.placeholder || f.label}</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : f.type === 'file' ? (
                    <input
                      id={`f-${f.name}`}
                      type="file"
                      name={f.name}
                      accept={f.accept}
                      className={`ad-input-file ${errors[f.name] ? 'ad-input-error' : ''}`}
                      onChange={change}
                    />
                  ) : (
                    <input
                      id={`f-${f.name}`}
                      type={f.type || 'text'}
                      name={f.name}
                      placeholder={f.placeholder || f.label}
                      step={f.type === 'number' ? 'any' : undefined}
                      disabled={f.disabled}
                      className={`ad-input-sm ${f.icon ? 'ad-input-has-icon' : ''} ${
                        errors[f.name] ? 'ad-input-error' : ''
                      }`}
                      value={values[f.name] ?? ''}
                      onChange={change}
                    />
                  )}
                </span>
                {errors[f.name] && <span className="ad-error-text">{errors[f.name]}</span>}
              </div>
            )
          )}
        </div>
      </form>
    </Modal>
  );
};

const ACTIONS = ['canView', 'canCreate', 'canEdit', 'canDelete'];

/** Module x action grid. Each row is one module; the header toggles a column. */
const PermissionMatrix = ({ label, modules, moduleLabel, value, onChange }) => {
  const { t } = useTranslation();

  const setCell = (module, action, checked) =>
    onChange(value.map((row) => (row.module === module ? { ...row, [action]: checked } : row)));

  const toggleRow = (module, checked) =>
    onChange(
      value.map((row) =>
        row.module === module
          ? { ...row, canView: checked, canCreate: checked, canEdit: checked, canDelete: checked }
          : row
      )
    );

  const allInRow = (row) => ACTIONS.every((a) => row[a]);

  return (
    <div className="ad-modal-field-full">
      <label className="ad-modal-label">{label}</label>
      <div className="ad-perm-table">
        <div className="ad-perm-row ad-perm-head">
          <span className="ad-perm-module">{t('staff.permissions.module')}</span>
          {ACTIONS.map((action) => (
            <span key={action} className="ad-perm-cell">
              {t(`staff.permissions.${action}`)}
            </span>
          ))}
          <span className="ad-perm-cell">{t('staff.permissions.all')}</span>
        </div>

        {value.map((row) => (
          <div key={row.module} className="ad-perm-row">
            <span className="ad-perm-module">
              {moduleLabel ? moduleLabel(row.module) : row.module}
            </span>
            {ACTIONS.map((action) => (
              <span key={action} className="ad-perm-cell">
                <input
                  type="checkbox"
                  className="ad-checkbox"
                  checked={Boolean(row[action])}
                  onChange={(e) => setCell(row.module, action, e.target.checked)}
                />
              </span>
            ))}
            <span className="ad-perm-cell">
              <input
                type="checkbox"
                className="ad-checkbox"
                checked={allInRow(row)}
                onChange={(e) => toggleRow(row.module, e.target.checked)}
              />
            </span>
          </div>
        ))}
      </div>
      <span className="ad-hint">{t('staff.permissions.hint')}</span>
    </div>
  );
};

export default RecordFormModal;
